import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from '../CSS/ApiComponent.module.css';
import {
  Search,
  Smartphone,
  Users,
  Filter,
  UserPlus,
  UserMinus,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Info,
  ChevronDown,
  ChevronRight,
  X,
  RotateCw,
  Battery,
  HardDrive
} from 'lucide-react';

const ApiComponent = () => {
    const [devices, setDevices] = useState([]);
    const [groups, setGroups] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilterGroup, setSelectedFilterGroup] = useState('');
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [selectedAddGroup, setSelectedAddGroup] = useState('');
    const [availableGroups, setAvailableGroups] = useState([]);
    const [assignedGroups, setAssignedGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [deviceDetails, setDeviceDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // URL base de la API - CORREGIDA
    const API_BASE_URL = 'https://truebavirtual.onrender.com/api/jamf';

    // Carga inicial de datos
    useEffect(() => {
        fetchDevicesAndGroups();
    }, [refreshTrigger]);

    const fetchDevicesAndGroups = async () => {
        setLoading(true);
        try {
            const devicesResponse = await axios.get(`${API_BASE_URL}/devices`, {
                params: { hasOwner: true },
            });
            const groupsResponse = await axios.get(`${API_BASE_URL}/groups`);
            setDevices(devicesResponse.data.devices || []);
            setGroups(groupsResponse.data.deviceGroups || []);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
            showMessage(`Error al cargar datos: ${err.message}`, 'error');
        }
    };

    // Actualizar grupos disponibles y asignados cuando se selecciona un dispositivo
    useEffect(() => {
        if (selectedDevice) {
            const device = devices.find(d => d.UDID === selectedDevice.UDID);
            if (device) {
                const deviceGroupIds = device.groupIds || [];
                
                // Grupos ya asignados al dispositivo
                const currentGroups = groups.filter(g => 
                    deviceGroupIds.includes(g.id.toString())
                );
                setAssignedGroups(currentGroups);
                
                // Grupos disponibles para asignar (no asignados aún)
                const groupsToAdd = groups.filter(g => 
                    !deviceGroupIds.includes(g.id.toString())
                );
                setAvailableGroups(groupsToAdd);
            }
        } else {
            setAssignedGroups([]);
            setAvailableGroups([]);
        }
    }, [selectedDevice, groups, devices]);

    const showMessage = (msg, type = 'info') => {
        setMessage(msg);
        setMessageType(type);
        setTimeout(() => {
            setMessage('');
        }, 5000);
    };

    const handleAddToGroup = async () => {
        if (!selectedDevice || !selectedAddGroup) {
            showMessage('Selecciona un dispositivo y un grupo para añadir.', 'error');
            return;
        }

        try {
            await axios.post(`${API_BASE_URL}/groups/add`, {
                groupId: selectedAddGroup,
                udids: [selectedDevice.UDID]
            });
            showMessage('Dispositivo agregado exitosamente al grupo.', 'success');
            setSelectedAddGroup('');
            // Refrescar datos
            setRefreshTrigger(prev => prev + 1);
        } catch (err) {
            showMessage(`Error al agregar dispositivo: ${err.message}`, 'error');
        }
    };

    const handleRemoveFromGroup = async (groupId, groupName) => {
        if (!selectedDevice) {
            showMessage('Selecciona un dispositivo primero.', 'error');
            return;
        }

        if (!window.confirm(`¿Estás seguro de que quieres quitar "${selectedDevice.name}" del grupo "${groupName}"?`)) {
            return;
        }

        try {
            await axios.post(`${API_BASE_URL}/groups/remove`, {
                groupId: groupId,
                udids: [selectedDevice.UDID]
            });
            showMessage('Dispositivo eliminado exitosamente del grupo.', 'success');
            // Refrescar datos
            setRefreshTrigger(prev => prev + 1);
        } catch (err) {
            showMessage(`Error al quitar dispositivo del grupo: ${err.message}`, 'error');
        }
    };

    const handleDeviceSelect = async (device) => {
        setSelectedDevice(device);
        // Cargar detalles del dispositivo
        await fetchDeviceDetails(device.UDID);
    };

    const fetchDeviceDetails = async (udid) => {
        setLoadingDetails(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/devices/${udid}`);
            console.log('Respuesta completa de la API:', response.data);
            // La respuesta tiene la estructura { code: 200, device: {...} }
            setDeviceDetails(response.data.device || response.data);
        } catch (err) {
            console.error('Error al cargar detalles del dispositivo:', err);
            setDeviceDetails(null);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleRestart = async () => {
        if (!selectedDevice) return;

        if (!window.confirm(`¿Estás seguro de que quieres reiniciar ${selectedDevice.name}?`)) {
            return;
        }

        try {
            await axios.post(`${API_BASE_URL}/devices/restart`, {
                udid: selectedDevice.UDID
            });
            showMessage('Comando de reinicio enviado exitosamente.', 'success');
        } catch (err) {
            showMessage(`Error al reiniciar dispositivo: ${err.message}`, 'error');
        }
    };

    const getFilteredDevices = () => {
        return devices.filter(device => {
            const matchesSearch = searchTerm === '' || 
                device.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                device.serialNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                device.owner?.name?.toLowerCase().includes(searchTerm.toLowerCase());
                
            const matchesGroup = selectedFilterGroup === '' || 
                (device.groups && device.groups.includes(selectedFilterGroup));
                
            return matchesSearch && matchesGroup;
        });
    };

    if (loading) return (
        <div className={styles.loadingContainer}>
            <RefreshCw className={styles.loadingIcon} />
            <p>Cargando datos...</p>
        </div>
    );
    
    if (error) return (
        <div className={styles.errorContainer}>
            <AlertCircle />
            <p>Error: {error}</p>
            <button 
                className={styles.retryButton} 
                onClick={() => setRefreshTrigger(prev => prev + 1)}
            >
                Reintentar
            </button>
        </div>
    );

    return (
        <div className={styles.apiComponent}>
            {message && (
                <div className={`${styles.message} ${styles[messageType]}`}>
                    {messageType === 'success' && <CheckCircle size={18} />}
                    {messageType === 'error' && <AlertCircle size={18} />}
                    {messageType === 'info' && <Info size={18} />}
                    <p>{message}</p>
                </div>
            )}

            <div className={styles.layoutContainer}>
                {/* Panel de filtros y lista de dispositivos */}
                <div className={styles.devicesPanel}>
                    <div className={styles.filterControls}>
                        <div className={styles.searchContainer}>
                            <Search className={styles.searchIcon} size={18} />
                            <input
                                type="text"
                                placeholder="Buscar por nombre, serie o propietario"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className={styles.searchInput}
                            />
                            {searchTerm && (
                                <button 
                                    className={styles.clearSearch} 
                                    onClick={() => setSearchTerm('')}
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                        
                        <div className={styles.selectContainer}>
                            <Users className={styles.selectIcon} size={18} />
                            <select 
                                onChange={e => setSelectedFilterGroup(e.target.value)} 
                                value={selectedFilterGroup}
                                className={styles.select}
                            >
                                <option value="">Todos los grupos</option>
                                {groups.map(group => (
                                    <option key={group.id} value={group.name}>
                                        {group.name} ({group.members})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    
                    <div className={styles.deviceListContainer}>
                        <h3 className={styles.panelTitle}>
                            <Smartphone size={18} />
                            <span>Dispositivos ({getFilteredDevices().length})</span>
                        </h3>
                        
                        {getFilteredDevices().length === 0 ? (
                            <div className={styles.emptyState}>
                                <p>No se encontraron dispositivos con los filtros actuales</p>
                                <button 
                                    className={styles.resetButton}
                                    onClick={() => {
                                        setSearchTerm('');
                                        setSelectedFilterGroup('');
                                    }}
                                >
                                    Resetear filtros
                                </button>
                            </div>
                        ) : (
                            <ul className={styles.deviceList}>
                                {getFilteredDevices().map(device => (
                                    <li 
                                        key={device.UDID} 
                                        className={`${styles.deviceItem} ${selectedDevice && selectedDevice.UDID === device.UDID ? styles.activeDevice : ''}`}
                                        onClick={() => handleDeviceSelect(device)}
                                    >
                                        <Smartphone size={18} />
                                        <div className={styles.deviceInfo}>
                                            <span className={styles.deviceName}>{device.name}</span>
                                            <div className={styles.deviceDetails}>
                                                <span className={styles.deviceSerial}>{device.serialNumber}</span>
                                                <span className={styles.deviceOwner}>{device.owner?.name || 'Sin dueño'}</span>
                                            </div>
                                            {device.groups && device.groups.length > 0 && (
                                                <div className={styles.deviceGroupTags}>
                                                    {device.groups.slice(0, 2).map(group => (
                                                        <span key={group} className={styles.groupTag}>{group}</span>
                                                    ))}
                                                    {device.groups.length > 2 && (
                                                        <span className={styles.groupTagMore}>+{device.groups.length - 2}</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <ChevronRight size={18} className={styles.deviceArrow} />
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
                
                {/* Panel de gestión de grupos */}
                <div className={styles.groupsPanel}>
                    {!selectedDevice ? (
                        <div className={styles.noDeviceSelected}>
                            <Smartphone size={48} />
                            <p>Selecciona un dispositivo para gestionar sus grupos</p>
                        </div>
                    ) : (
                        <>
                            <div className={styles.selectedDeviceHeader}>
                                <h3>{selectedDevice.name}</h3>
                                <span className={styles.deviceSerialBadge}>{selectedDevice.serialNumber}</span>
                                <button
                                    className={styles.deselectButton}
                                    onClick={() => {
                                        setSelectedDevice(null);
                                        setDeviceDetails(null);
                                    }}
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            <div className={styles.groupManagementContainer}>
                                {/* Sección de información del dispositivo */}
                                <div className={styles.deviceInfoSection}>
                                    <div className={styles.sectionTitleWithButton}>
                                        <h4 className={styles.sectionTitle}>
                                            <Smartphone size={16} />
                                            <span>Información del dispositivo</span>
                                        </h4>
                                        <button
                                            className={styles.refreshDetailsButton}
                                            onClick={() => fetchDeviceDetails(selectedDevice.UDID)}
                                            disabled={loadingDetails}
                                            title="Actualizar información"
                                        >
                                            <RefreshCw size={14} className={loadingDetails ? styles.spinning : ''} />
                                        </button>
                                    </div>

                                    {loadingDetails ? (
                                        <div className={styles.loadingDetailsMessage}>
                                            <RefreshCw size={16} className={styles.loadingIcon} />
                                            <span>Cargando información...</span>
                                        </div>
                                    ) : deviceDetails ? (
                                        <div className={styles.deviceDetailsGrid}>
                                            {/* Batería - puede venir como porcentaje entero (61) o decimal (0.61) */}
                                            {deviceDetails.batteryLevel !== undefined && deviceDetails.batteryLevel !== null && (
                                                <div className={styles.deviceDetailItem}>
                                                    <Battery size={16} className={styles.detailIcon} />
                                                    <div className={styles.detailContent}>
                                                        <span className={styles.detailLabel}>Batería</span>
                                                        <span className={styles.detailValue}>
                                                            {deviceDetails.batteryLevel > 1
                                                                ? Math.round(deviceDetails.batteryLevel)
                                                                : Math.round(deviceDetails.batteryLevel * 100)
                                                            }%
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Almacenamiento disponible - availableCapacity en GB */}
                                            {deviceDetails.availableCapacity !== undefined && deviceDetails.availableCapacity !== null && (
                                                <div className={styles.deviceDetailItem}>
                                                    <HardDrive size={16} className={styles.detailIcon} />
                                                    <div className={styles.detailContent}>
                                                        <span className={styles.detailLabel}>Almacenamiento disponible</span>
                                                        <span className={styles.detailValue}>
                                                            {parseFloat(deviceDetails.availableCapacity).toFixed(2)} GB
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Almacenamiento usado - calculado */}
                                            {deviceDetails.totalCapacity !== undefined && deviceDetails.totalCapacity !== null &&
                                             deviceDetails.availableCapacity !== undefined && deviceDetails.availableCapacity !== null && (
                                                <div className={styles.deviceDetailItem}>
                                                    <HardDrive size={16} className={styles.detailIcon} />
                                                    <div className={styles.detailContent}>
                                                        <span className={styles.detailLabel}>Almacenamiento usado</span>
                                                        <span className={styles.detailValue}>
                                                            {(parseFloat(deviceDetails.totalCapacity) - parseFloat(deviceDetails.availableCapacity)).toFixed(2)} GB
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Capacidad total - totalCapacity en GB */}
                                            {deviceDetails.totalCapacity !== undefined && deviceDetails.totalCapacity !== null && (
                                                <div className={styles.deviceDetailItem}>
                                                    <HardDrive size={16} className={styles.detailIcon} />
                                                    <div className={styles.detailContent}>
                                                        <span className={styles.detailLabel}>Capacidad total</span>
                                                        <span className={styles.detailValue}>
                                                            {parseFloat(deviceDetails.totalCapacity).toFixed(2)} GB
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Si no hay datos relevantes, mostrar mensaje */}
                                            {(deviceDetails.batteryLevel === undefined || deviceDetails.batteryLevel === null) &&
                                             (deviceDetails.availableCapacity === undefined || deviceDetails.availableCapacity === null) &&
                                             (deviceDetails.totalCapacity === undefined || deviceDetails.totalCapacity === null) && (
                                                <p className={styles.noDetailsMessage}>
                                                    La información de batería y almacenamiento no está disponible para este dispositivo
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <p className={styles.noDetailsMessage}>No se pudo cargar la información del dispositivo</p>
                                    )}

                                    {/* Botones de control */}
                                    <div className={styles.deviceControlButtons}>
                                        <button
                                            className={styles.restartButton}
                                            onClick={handleRestart}
                                            title="Reiniciar dispositivo"
                                        >
                                            <RotateCw size={16} />
                                            <span>Reiniciar dispositivo</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Sección de grupos asignados */}
                                <div className={styles.assignedGroupsSection}>
                                    <h4 className={styles.sectionTitle}>
                                        <Users size={16} />
                                        <span>Grupos asignados ({assignedGroups.length})</span>
                                    </h4>
                                    
                                    {assignedGroups.length === 0 ? (
                                        <p className={styles.noGroupsMessage}>Este dispositivo no pertenece a ningún grupo</p>
                                    ) : (
                                        <ul className={styles.groupsList}>
                                            {assignedGroups.map(group => (
                                                <li key={group.id} className={styles.groupItem}>
                                                    <div className={styles.groupInfo}>
                                                        <span className={styles.groupName}>{group.name}</span>
                                                        <span className={styles.groupMembers}>{group.members} dispositivos</span>
                                                    </div>
                                                    <button
                                                        className={styles.removeButton}
                                                        onClick={() => handleRemoveFromGroup(group.id, group.name)}
                                                        title="Quitar de este grupo"
                                                    >
                                                        <UserMinus size={16} />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                
                                {/* Sección para añadir a grupos */}
                                <div className={styles.addGroupSection}>
                                    <h4 className={styles.sectionTitle}>
                                        <UserPlus size={16} />
                                        <span>Añadir a un grupo</span>
                                    </h4>
                                    
                                    {availableGroups.length === 0 ? (
                                        <p className={styles.noGroupsMessage}>El dispositivo ya pertenece a todos los grupos disponibles</p>
                                    ) : (
                                        <div className={styles.addGroupForm}>
                                            <div className={styles.groupSelectContainer}>
                                                <select 
                                                    onChange={e => setSelectedAddGroup(e.target.value)} 
                                                    value={selectedAddGroup}
                                                    className={styles.groupSelect}
                                                >
                                                    <option value="">Selecciona un grupo</option>
                                                    {availableGroups.map(group => (
                                                        <option key={group.id} value={group.id}>
                                                            {group.name} ({group.members} dispositivos)
                                                        </option>
                                                    ))}
                                                </select>
                                                <ChevronDown size={16} className={styles.selectArrow} />
                                            </div>
                                            
                                            <button 
                                                className={styles.addButton}
                                                onClick={handleAddToGroup}
                                                disabled={!selectedAddGroup}
                                            >
                                                <UserPlus size={16} />
                                                <span>Añadir</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApiComponent;